<template>
  <div class="lazy-image-container" :class="containerClass">
    <img
      ref="imageRef"
      :data-src="src"
      :alt="alt"
      :class="['lazy-image', imageClass, { 'loaded': isLoaded, 'error': error }]"
      :style="imageStyle"
      @load="onLoad"
      @error="onError"
    />
    
    <!-- Loading placeholder -->
    <div v-if="!isLoaded && !error" class="lazy-image-placeholder">
      <div class="lazy-image-spinner"></div>
    </div>
    
    <!-- Error fallback -->
    <div v-if="error" class="lazy-image-error">
      <i class="fas fa-image-slash"></i>
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { useLazyImage } from '~/composables/useLazyImage'

const props = defineProps({
  src: {
    type: String,
    required: true
  },
  alt: {
    type: String,
    default: ''
  },
  containerClass: {
    type: String,
    default: ''
  },
  imageClass: {
    type: String,
    default: ''
  },
  imageStyle: {
    type: Object,
    default: () => ({})
  },
  errorMessage: {
    type: String,
    default: 'Failed to load image'
  }
})

const emit = defineEmits(['load', 'error'])

const { imageRef, isLoaded, error } = useLazyImage({
  rootMargin: '100px',
  threshold: 0.01
})

const onLoad = () => {
  emit('load')
}

const onError = () => {
  emit('error')
}
</script>

<style scoped>
.lazy-image-container {
  position: relative;
  overflow: hidden;
  background-color: #f3f4f6;
}

.lazy-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.lazy-image.loaded {
  opacity: 1;
}

.lazy-image.error {
  display: none;
}

.lazy-image-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.lazy-image-spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.lazy-image-error {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fef2f2;
  color: #991b1b;
  gap: 0.5rem;
  padding: 1rem;
}

.lazy-image-error i {
  font-size: 2rem;
}

.lazy-image-error span {
  font-size: 0.875rem;
  text-align: center;
}
</style>

